import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import { opsSupplierApi, type OpsPublishTarget, type OpsSupplier } from "@/api/opsSupplierClient";
import { ApiError, RequestTimeoutError, keysApi, usageApi } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SupplierAccountPanel, SupplierKeyPanel } from "@/components/supplier/OpsSupplierPanels";
import { OperatorUsageCell } from "@/components/usage/OperatorUsageCell";
import { Pagination } from "@/components/Pagination";
import { useTranslation } from "@/framework-originated/i18n";

// /suppliers/:id — 运营端供应商详情(doc/049 P5/P6):账号/Key、
// 审计查询(P6,复用 AuditLogHandler 形态)、分发记录(未脱敏,D3)。
type Tab = "account" | "keys" | "publishTargets" | "usage" | "audit" | "records";

const STATUS_VARIANT: Record<string, "default" | "success" | "warning" | "destructive" | "muted"> = {
  success: "success",
  failed: "destructive",
  pending: "warning",
  dispatching: "warning",
  unknown: "muted",
  abandoned: "muted",
};

export function SupplierDetailPage() {
  const { id } = useParams({ from: "/shell/suppliers/$id" });
  const numId = Number(id);
  const qc = useQueryClient();
  // nsSeparator:false(i18n 配置允许 key 内冒号),命名空间经数组回退查找;
  // 状态标签复用 supplier 命名空间的 status.*。
  const { t } = useTranslation(["opsSuppliers", "supplier"]);
  const [tab, setTab] = useState<Tab>("account");
  const [msg, setMsg] = useState<string | null>(null);

  const [pageSize, setPageSize] = useState(20);
  const [usagePage, setUsagePage] = useState(1);
  const [usagePageSize, setUsagePageSize] = useState(20);

  const supplierQ = useQuery({
    queryKey: ["ops-supplier", numId],
    queryFn: () => opsSupplierApi.get(numId),
  });

  const publishTargetsQ = useQuery({
    queryKey: ["ops-supplier-publish-targets", numId],
    queryFn: () => opsSupplierApi.listPublishTargets(numId),
    enabled: tab === "publishTargets",
  });

  const supplierKeysQ = useQuery({
    queryKey: ["ops-supplier-usage-keys", numId, usagePage, usagePageSize],
    queryFn: () => keysApi.list({ owner_supplier_id: numId, page: usagePage, page_size: usagePageSize }),
    enabled: tab === "usage",
  });
  const usageKeyIds = useMemo(() => (supplierKeysQ.data?.items ?? []).map((key) => key.id), [supplierKeysQ.data]);
  const usageQ = useQuery({
    queryKey: ["ops-supplier-channel-usage", numId, usageKeyIds],
    queryFn: () => usageApi.listChannelSummaries(usageKeyIds),
    enabled: tab === "usage" && usageKeyIds.length > 0,
  });
  const usageByKey = useMemo(
    () => new Map((usageQ.data?.items ?? []).map((summary) => [summary.key_id, summary])),
    [usageQ.data],
  );
  const usageTotal = supplierKeysQ.data?.total ?? 0;

  const auditQ = useQuery({
    queryKey: ["ops-supplier-audit", numId],
    queryFn: () => opsSupplierApi.listAudit(numId, { page: 1, page_size: pageSize }),
    enabled: tab === "audit",
  });

  const recordsQ = useQuery({
    queryKey: ["ops-supplier-records", numId],
    queryFn: () => opsSupplierApi.listRecords(numId, { page: 1, page_size: pageSize }),
    enabled: tab === "records",
  });

  const refreshUsageMut = useMutation({
    mutationFn: ({ keyIds }: { keyIds: number[]; scope: "page" | "row" }) => usageApi.refresh(keyIds),
    onSuccess: async (result) => {
      await usageQ.refetch();
      setMsg(result.failed_count > 0 || result.partial_count > 0
        ? `用量刷新完成（部分异常）：成功 ${result.success_count}，部分 ${result.partial_count}，失败 ${result.failed_count}`
        : `用量刷新完成：${result.key_count} 个 Key、${result.source_count} 个分发目标`);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) setMsg("已有用量刷新正在进行，请稍后再试");
      else if (error instanceof RequestTimeoutError) setMsg(error.message);
      else setMsg(error instanceof ApiError ? error.message : "用量刷新失败");
    },
  });

  const s: OpsSupplier | undefined = supplierQ.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/suppliers" className="text-xs text-muted-foreground underline-offset-2 hover:underline">
            ← {t("detail.back")}
          </Link>
          <h1 className="mt-1 text-xl font-semibold">
            {t("detail.title")} · {s ? `${s.username} (${s.code_prefix})` : `#${numId}`}
          </h1>
          {s && (
            <p className="mt-0.5 text-xs text-muted-foreground">
{t("detail.account")}: {s.username} · {t("detail.createdAt")}: {new Date(s.created_at).toLocaleString()} ·{" "}
      {s.enabled ? t("statusEnabled") : t("statusDisabled")}
            </p>
          )}
        </div>
        <LanguageSwitcher />
      </div>

      {msg && <div className="text-sm text-muted-foreground">{msg}</div>}

      <div className="flex gap-1 overflow-x-auto border-b">
        {(["account", "keys", "publishTargets", "usage", "audit", "records"] as Tab[]).map((k) => (
          <button
            key={k}
            onClick={() => { setTab(k); setMsg(null); }}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === k ? "border-primary font-medium text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t(`detail.tabs.${k}`)}
          </button>
        ))}
      </div>

      {tab === "account" && s && (
        <SupplierAccountPanel supplier={s} onChanged={async (message) => {
          setMsg(message);
          await qc.invalidateQueries({ queryKey: ["ops-supplier", numId] });
          await qc.invalidateQueries({ queryKey: ["ops-suppliers"] });
        }} />
      )}

      {tab === "keys" && s && <SupplierKeyPanel supplier={s} />}

      {tab === "publishTargets" && (
        <Card>
          <CardContent className="pt-6">
            <p className="mb-4 text-xs text-muted-foreground">当前启用模板形成的完整发布窗口。指定编号来自目标分配，自动编号由 Supplier 与真实站点稳定计算。</p>
            {publishTargetsQ.isLoading && <div className="text-sm text-muted-foreground">加载中…</div>}
            {publishTargetsQ.isError && <div className="text-sm text-destructive">发布对照加载失败</div>}
            {publishTargetsQ.data?.items.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">暂无可发布站点</div>}
            {!!publishTargetsQ.data?.items.length && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="py-2">发布编号</th><th>来源</th><th>真实站点</th><th>站点编码</th><th>模板</th><th>可发布</th>
                    </tr>
                  </thead>
                  <tbody>
                    {publishTargetsQ.data.items.map((target: OpsPublishTarget) => (
                      <tr key={`${target.site_type}:${target.site_id}`} className="border-b hover:bg-muted/30">
                        <td className="py-2 font-mono font-medium">{target.target_code}</td>
                        <td><Badge variant="muted">{target.code_source === "explicit" ? "指定" : "自动"}</Badge></td>
                        <td>{target.site_name || `#${target.site_id}`} <span className="text-xs text-muted-foreground">({target.site_type === "relay" ? "中转站" : "推送站"} #{target.site_id})</span></td>
                        <td className="font-mono text-xs">{target.site_code || "—"}</td>
                        <td className="text-xs">{target.templates.map((template) => template.name || template.code).join("、") || "—"}</td>
                        <td>{target.publishable ? <Badge variant="success">是</Badge> : <Badge variant="muted">否</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "usage" && (
        <Card>
          <CardContent className="pt-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-medium">Supplier 自有上游 Key 最新累计用量</div>
                <p className="text-xs text-muted-foreground">展示当前 usage_current 投影；展开用量可查看真实站点与任务快照中的 Supplier 发布编号。</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={usageKeyIds.length === 0 || refreshUsageMut.isPending}
                onClick={() => refreshUsageMut.mutate({ keyIds: usageKeyIds, scope: "page" })}
              >
                <RefreshCw className={`mr-1 h-3.5 w-3.5 ${refreshUsageMut.isPending && refreshUsageMut.variables?.scope === "page" ? "animate-spin" : ""}`} />
                刷新本页用量
              </Button>
            </div>
            {supplierKeysQ.isLoading && <div className="text-sm text-muted-foreground">加载中…</div>}
            {supplierKeysQ.isError && <div className="text-sm text-destructive">Supplier Key 加载失败</div>}
            {!supplierKeysQ.isLoading && usageTotal === 0 && <div className="py-8 text-center text-sm text-muted-foreground">该 Supplier 暂无自有 Key</div>}
            {usageTotal > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs text-muted-foreground">
                    <tr><th className="py-2">Key</th><th>提示</th><th>状态</th><th>最新累计用量</th><th>覆盖</th><th>最近采集</th></tr>
                  </thead>
                  <tbody>
                    {(supplierKeysQ.data?.items ?? []).map((key) => {
                      const summary = usageByKey.get(key.id);
                      return (
                        <tr key={key.id} className="border-b hover:bg-muted/30">
                          <td className="py-2"><div className="font-medium">{key.name}</div><div className="font-mono text-[10px] text-muted-foreground">#{key.id}</div></td>
                          <td className="font-mono text-xs">{key.key_hint || "—"}</td>
                          <td><Badge variant={key.status === "active" ? "success" : "muted"}>{key.status}</Badge></td>
                          <td>
                            <OperatorUsageCell
                              summary={summary}
                              loading={usageQ.isLoading}
                              failed={usageQ.isError}
                              refreshing={refreshUsageMut.isPending && refreshUsageMut.variables?.scope === "row" && refreshUsageMut.variables.keyIds[0] === key.id}
                              refreshDisabled={refreshUsageMut.isPending}
                              onRefresh={() => refreshUsageMut.mutate({ keyIds: [key.id], scope: "row" })}
                            />
                          </td>
                          <td className="text-xs">{summary ? `${summary.valued_source_count}/${summary.source_site_count} · ${summary.coverage}` : "—"}</td>
                          <td className="text-xs text-muted-foreground">{summary?.latest_sampled_at ? new Date(summary.latest_sampled_at).toLocaleString() : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination page={usagePage} total={supplierKeysQ.data?.total} pageSize={usagePageSize}
              onPageChange={setUsagePage} onPageSizeChange={setUsagePageSize} loading={supplierKeysQ.isFetching} />
          </CardContent>
        </Card>
      )}

      {tab === "audit" && (
        <Card>
          <CardContent className="pt-6">
            {auditQ.isLoading && <div className="text-sm text-muted-foreground">…</div>}
            {auditQ.data && auditQ.data.items.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">{t("detail.noAudit")}</div>
            )}
            {auditQ.data && auditQ.data.items.length > 0 && (
              <table className="w-full text-sm">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="py-2">ID</th>
                    <th>{t("detail.auditAction")}</th>
                    <th>{t("detail.auditTarget")}</th>
                    <th>{t("detail.auditKey")}</th>
                    <th>{t("detail.auditNote")}</th>
                    <th>{t("detail.auditBy")}</th>
                    <th>{t("detail.auditIP")}</th>
                    <th>{t("detail.auditWhen")}</th>
                  </tr>
                </thead>
                <tbody>
                  {auditQ.data.items.map((a) => (
                    <tr key={a.id} className="border-b hover:bg-muted/30">
                      <td className="py-2 font-mono text-xs">{a.id}</td>
                      <td><Badge variant="muted">{a.action}</Badge></td>
                      <td className="text-xs">{a.target_type}</td>
                      <td className="text-xs text-muted-foreground">{a.target_key || "—"}</td>
                      <td className="text-xs">{a.note || "—"}</td>
                      <td className="text-xs">{a.operator_name || a.operator_id}</td>
                      <td className="font-mono text-xs">{a.ip || "—"}</td>
                      <td className="text-xs text-muted-foreground">{new Date(a.occurred_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "records" && (
        <Card>
          <CardContent className="pt-6">
            {recordsQ.isLoading && <div className="text-sm text-muted-foreground">…</div>}
            {recordsQ.data && recordsQ.data.items.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">{t("detail.noRecords")}</div>
            )}
            {recordsQ.data && recordsQ.data.items.length > 0 && (
              <table className="w-full text-sm">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="py-2">ID</th>
                    <th>{t("detail.recordKey")}</th>
                    <th>{t("detail.recordTarget")}</th>
                    <th>site</th>
                    <th>{t("detail.recordStatus")}</th>
                    <th>{t("detail.recordRetries")}</th>
                    <th>{t("detail.recordError")}</th>
                    <th>{t("detail.recordCreated")}</th>
                  </tr>
                </thead>
                <tbody>
                  {recordsQ.data.items.map((r) => (
                    <tr key={r.id} className="border-b hover:bg-muted/30">
                      <td className="py-2 font-mono text-xs">{r.id}</td>
                      <td>{r.key_name || `#${r.key_id}`}</td>
                      <td className="font-mono text-xs">{r.target_code || `${r.site_type}/${r.site_code}`}</td>
                      <td className="text-xs text-muted-foreground">{r.site_name || r.site_code || "—"}</td>
                      <td>
<Badge variant={STATUS_VARIANT[r.status] ?? "muted"}>
  {t(`status.${r.status}`)}
</Badge>
                      </td>
                      <td className="text-xs">{r.retry_count}/{r.max_retries}</td>
                      <td className="text-xs text-destructive">{r.error_message || "—"}</td>
                      <td className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
